import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiUser } from 'react-icons/fi';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import S from './Widget.module.scss';
import { AiFillPhone } from 'react-icons/ai';
import { MdEmail, MdPerson } from 'react-icons/md';
import { IoIosContacts } from 'react-icons/io';

const CONTACT_LABELS = {
  operation: 'Opérations',
  data_manager: 'Data Manager',
  general: 'Contact Général',
};

const HARDCODED_CONTACTS = [
  {
    id: 'operations',
    type: 'operation',
    name: 'M Xavier Lelong',
    phone: '06 21 43 68 97',
    email: 'xavier.lelong@itmsol.fr',
    company_name: '',
  },
  {
    id: 'general',
    type: 'general',
    name: 'Contact Général',
    phone: '01 41 06 48 98',
    email: 'contact@itmsol.fr',
    company_name: 'ITM Solutions',
  },
  {
    id: 'data_manager',
    type: 'data_manager',
    name: 'M Maxance Lu',
    phone: '06 68 09 23 94',
    email: 'maxance.lu@itmsol.fr',
    company_name: '',
  },
];

const extractContacts = (projects) => {
  if (!Array.isArray(projects) || projects.length === 0) return [];
  const seen = new Set();
  const result = [];

  const add = (contact, type) => {
    if (!contact || !contact.user_id) return;
    const key = `${type}-${contact.user_id}`;
    if (seen.has(key)) return;
    seen.add(key);
    const phone = [contact.phone_index, contact.phone_number]
      .filter(Boolean)
      .join(' ')
      .trim();
    result.push({
      id: key,
      type,
      name: `${contact.name || ''} ${contact.lastname || ''}`.trim(),
      email: contact.email || '',
      phone,
      company_name: contact.company_name || '',
    });
  };

  projects.forEach((project) => {
    const { operation = [], data_manager = [], general } = project.contacts || {};
    operation.forEach((c) => add(c, 'operation'));
    data_manager.forEach((c) => add(c, 'data_manager'));
    add(general, 'general');
  });

  return result;
};

const sanitizePhone = (phone) => phone.replace(/\s+/g, '');

const WidgetContact = ({ projects = [], loading = false }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  const contacts = useMemo(() => {
    const real = extractContacts(projects);
    return real.length > 0 ? real : HARDCODED_CONTACTS;
  }, [projects]);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setHasOverflow(maxScroll > 1);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, contacts]);

  const getScrollAmount = () => {
    const el = scrollRef.current;
    if (!el) return 300;
    const card = el.querySelector('[data-contact-card]');
    const styles = window.getComputedStyle(el);
    const gapValue = parseFloat(styles.columnGap || styles.gap || '0');
    const cardWidth = card ? card.getBoundingClientRect().width : el.clientWidth;
    return cardWidth + (Number.isFinite(gapValue) ? gapValue : 0);
  };

  const handleScrollBy = (direction) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * getScrollAmount(), behavior: 'smooth' });
  };

  return (
    <div className={[S.widget, S.widgetSmall, S.widgetContactBox].join(' ')}>
      <div className={S.contactContainer}>
        <div className={S.contactHeader}>
          <div>
            <span className={S.contactTitle}>{checkMot('contacts')}</span>
          </div>
          <div className={S.contactIconCircle}>
            <FiUser size={18} />
          </div>
        </div>

        <div className={S.contactListWrapper}>
          {loading ? (
            <div className={S.widgetLoadingState}>
              <div className={S.widgetLoadingSpinner} />
              <span className={S.widgetLoadingText}>{checkMot('chargement_en_cours')}</span>
            </div>
          ) : (
            <>
              {hasOverflow && (
                <>
                  <button
                    type="button"
                    className={[S.scrollButton, S.scrollButtonLeft, !canScrollLeft ? S.scrollButtonDisabled : ''].join(' ')}
                    onClick={() => handleScrollBy(-1)}
                    aria-label="Faire défiler les contacts vers la gauche"
                    disabled={!canScrollLeft}
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    className={[S.scrollButton, S.scrollButtonRight, !canScrollRight ? S.scrollButtonDisabled : ''].join(' ')}
                    onClick={() => handleScrollBy(1)}
                    aria-label="Faire défiler les contacts vers la droite"
                    disabled={!canScrollRight}
                  >
                    <FiChevronRight size={16} />
                  </button>
                </>
              )}
              <div className={S.contactCarousel}>
                <div className={S.contactList} ref={scrollRef}>
                  {contacts.map((contact) => (
                    <div className={S.contactCard} key={contact.id} data-contact-card>
                      <div className={S.contactCardTitle}>
                        <IoIosContacts size={18} />
                        <span>{CONTACT_LABELS[contact.type] || contact.type}</span>
                      </div>
                      <div className={S.contactCardBody}>
                        <div className={S.contactLine}>
                          <MdPerson size={18} color="#eeb214" />
                          <span>{contact.name}</span>
                        </div>
                        {contact.company_name ? (
                          <div className={S.contactLine}>
                            <span className={S.contactCompany}>{contact.company_name}</span>
                          </div>
                        ) : null}
                        {contact.phone ? (
                          <div className={S.contactLine}>
                            <AiFillPhone size={18} color="#eeb214" />
                            <a href={`tel:${sanitizePhone(contact.phone)}`}>{contact.phone}</a>
                          </div>
                        ) : null}
                        {contact.email ? (
                          <div className={S.contactLine}>
                            <MdEmail size={18} color="#eeb214" />
                            <a href={`mailto:${contact.email}`}>{contact.email}</a>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetContact;
